import { OrderableType } from "./orders";
import {
  AddonPricingType,
  DiscountType,
  DynamicPriceRate,
  FeeLine,
  QuoteItemOrderableType,
  toDynamicPriceRate,
} from "../offering/api";
import { SpaceType } from "./space";

export interface Quote {
  totalPrice: number;
  orderItems: QuoteOrderItem[];
}

export interface QuoteItem {
  orderableType: QuoteItemOrderableType;
  quoteId: string;
  orderableId: string;
  orderable: Orderable;
  unitPrice: number;
  unitPriceAfterDiscount: number;
  unitPriceAfterDiscountWithTax: number;
  unitPriceWithTax: number;
  unitPriceAfterDiscountWithTaxAndFee: number;
  unitPriceWithTaxAndFee: number;
  totalPrice: number;
  quantity: number;
  date: string;
  bundled: boolean;
  subTotal: number;
  unitCount: number;
  // hourly booking
  startTime: string;
  endTime: string;
  // daily booking
  startDate: string;
  endDate: string;
  days: number;
  //original price
  originalUnitPrice: number;
  originalSubTotalPrice: number;
  originalTotalPrice: number;
  quoteItemPrices: QuoteItemPrice[];
  //promotion
  promotion?: Promotion;
  promotionFreeUnit: number;
  promotionAmount: number;
  dynamicPriceRate?: DynamicPriceRate;
  feeLines?: FeeLine[];
  //tax
  taxAmount: number;
  taxDescription?: string;
  taxPercentage?: number;
  //addOn
  spaceOfferingDate?: string;
  parentLineItemId?: string | null;
  // Set on every date of a recurring booking: the rule it belongs to and its
  // 1-based position in it (assigned in date order, never renumbered).
  bookingSeriesId?: string;
  occurrenceIndex?: number;
  // Set when an hourly booking covering the room's full bookable day was capped at
  // the room's daily rate. The original prices above still carry the uncapped hourly
  // cost, which is what the checkout shows the cap as a reduction from.
  dailyRateApplied?: boolean;
}

export interface Promotion {
  code: string;
  description: string;
  discountType: DiscountType;
  discountValue: number;
  orderNonRefundable: boolean;
}

export function toPromotion(data: any) {
  return {
    code: data.code,
    description: data.description,
    discountType: data.discount_type,
    discountValue: data.discount_value,
    orderNonRefundable: data.order_non_refundable,
  };
}
interface CommonQuoteItemPrice {
  id: string;
  quoteItemId: string;
  originalUnitPrice: number;
  unitPrice: number;
  unitCount: number;
  dynamicPriceRate?: DynamicPriceRate;
  totalPrice: number;
}

export interface DayUnitQuoteItemPrice extends CommonQuoteItemPrice {
  unitType: "day_unit";
  startDate: string;
  endDate: string;
}

interface HourUnitQuoteItemPrice extends CommonQuoteItemPrice {
  unitType: "hour_unit";
  startTime: string;
  endTime: string;
}

export type QuoteItemPrice = DayUnitQuoteItemPrice | HourUnitQuoteItemPrice;

export interface Orderable {
  id: string;
  name: string;
  numberOfEntries: number;
  spaceType: SpaceType;
  displayName: string;
  pricingType: AddonPricingType;
  daysToExpire?: number;
}

export function toOrderable(data: any): Orderable {
  return {
    id: data.id,
    name: data.name,
    numberOfEntries: data.number_of_entries,
    spaceType: data.space_type,
    displayName: data.display_name,
    pricingType: data.pricing_type,
    daysToExpire: data.days_to_expire,
  };
}

export function toQuoteItemPrice(data: any): QuoteItemPrice {
  return {
    id: data.id,
    startDate: data.start_date,
    endDate: data.end_date,
    quoteItemId: data.quote_item_id,
    originalUnitPrice: data.original_unit_price,
    unitCount: data.unit_count,
    unitPrice: data.unit_price,
    unitType: data.unit_type,
    dynamicPriceRate: data.dynamic_price_rate
      ? toDynamicPriceRate(data.dynamic_price_rate)
      : undefined,
    totalPrice: data.total_price,
    startTime: data.start_time,
    endTime: data.end_time,
  };
}

interface QuoteOrderItemHourlyBookingDetails {
  attendees: number;
  date: string;
  duration: number;
  spaceID: string;
  time: string;
}

interface QuoteOrderItemDailyBookingDetails {
  attendees: number;
  startDate: string;
  endDate: string;
  spaceID: string;
  days: number;
}

type QuoteOrderItemBookingDetails =
  | QuoteOrderItemHourlyBookingDetails
  | QuoteOrderItemDailyBookingDetails;

interface QuoteOrderItemBooking {
  orderableType: OrderableType.Booking;
  unitPrice: number;
  subTotal: number;
  details: QuoteOrderItemBookingDetails;
}

export type QuoteOrderItem = QuoteOrderItemBooking;
